import {
  Tag,
  Button,
  Tooltip,
  Space,
  Row,
  Col,
  Typography,
  Avatar,
} from "antd";
import { Eye, User } from "lucide-react";
import { format } from "date-fns";
import { BOOKING_STATUS_COLOR } from "commons/constants";
import { getBookingCodeDiscountLabel, getBookingTotal } from "./orderTotals.helpers";

const { Text } = Typography;

export const ORDER_STATUS_OPTIONS = [
  { value: "all", label: "All Statuses" },
  { value: "PAYMENT_PENDING", label: "Payment Pending" },
  { value: "PAYMENT_CONFIRMED", label: "Payment Confirmed" },
  { value: "CANCELLED", label: "Cancelled" },
  { value: "PAYMENT_FAILED", label: "Payment Failed" },
  { value: "PAYMENT_REFUNDED", label: "Refunded" },
];

export const BOOKING_STATUS_OPTIONS = [
  { value: "all", label: "All Statuses" },
  { value: "CONFIRMED", label: "Confirmed" },
  { value: "CANCELLED", label: "Cancelled" },
  { value: "NO_SHOW", label: "No Show" },
  { value: "ATTENDED", label: "Attended" },
];

export const BOOKING_FILTER_OPTIONS = [
  { value: "ALL", label: "All" },
  { value: "UPCOMING", label: "Upcoming" },
  { value: "PAST", label: "Past" },
];

const ORDER_STATUS_COLOR = {
  CONFIRMED: "success",
  PAYMENT_CONFIRMED: "success",
  PAYMENT_CONFIRMED_AND_INVOICE_GENERATED: "success",
  PAYMENT_CONFIRMED_AND_INVOICE_GENERATION_FAILED: "warning",
  CANCELLED: "error",
  PAYMENT_FAILED: "error",
  RESERVATION_EXPIRED: "error",
  PAYMENT_PENDING: "warning",
  PAYMENT_REFUNDED: "default",
  DRAFT: "default",
};

export function orderStatusTag(status) {
  return (
    <Tag
      color={ORDER_STATUS_COLOR[status] ?? "default"}
      style={{ fontSize: 11 }}
    >
      {status?.replace(/_/g, " ")}
    </Tag>
  );
}

export function bookingStatusTag(status) {
  return (
    <Tag
      color={BOOKING_STATUS_COLOR[status] ?? "default"}
      style={{ fontSize: 11 }}
    >
      {status}
    </Tag>
  );
}

export function getGrandTotal(order_totals) {
  const grand = order_totals?.find((t) => t.type === "GRAND_TOTAL");
  if (!grand) return "—";
  const { amount, currency } = grand.value ?? {};
  if (amount == null) return "—";
  return `${currency ?? ""} ${Number(amount).toFixed(2)}`.trim();
}

export function formatDate(str) {
  if (!str) return "—";
  try {
    return format(new Date(str), "MMM d, yyyy");
  } catch {
    return str;
  }
}

export function formatDateTime(str) {
  if (!str) return "—";
  try {
    return format(new Date(str), "MMM d, yyyy h:mm a");
  } catch {
    return str;
  }
}

/** Shared card used in both OrderDetail and BookingDetail */
export function InfoCard({ title, icon: Icon, children }) {
  return (
    <div className="rounded-2xl border border-border/50 bg-card p-5 shadow-card h-full">
      <Row align="middle" gutter={8} style={{ marginBottom: 16 }}>
        <Col>
          <div className="rounded-lg bg-muted p-1.5">
            <Icon className="h-4 w-4 text-muted-foreground" />
          </div>
        </Col>
        <Col>
          <Text strong>{title}</Text>
        </Col>
      </Row>
      {children}
    </div>
  );
}

/** Shared customer info card used in OrderDetail and BookingDetail */
export function CustomerCard({ customer_details: cd, customer_id }) {
  return (
    <InfoCard title="Customer" icon={User}>
      {cd ? (
        <Row gutter={[12, 0]} align="top" wrap={false}>
          <Col>
            <Avatar src={cd.profile_image_url} size={44}>
              {cd.full_name?.[0]?.toUpperCase() ?? "?"}
            </Avatar>
          </Col>
          <Col flex={1}>
            <Row gutter={[0, 4]}>
              <Col span={24}>
                <Text strong style={{ fontSize: 15 }}>
                  {cd.full_name || "—"}
                </Text>
              </Col>
              {cd.phone_number && (
                <Col span={24}>
                  <Text type="secondary">{cd.phone_number}</Text>
                </Col>
              )}
              {cd.email_id && (
                <Col span={24}>
                  <Text type="secondary">{cd.email_id}</Text>
                </Col>
              )}
            </Row>
          </Col>
        </Row>
      ) : (
        <Text type="secondary" code style={{ fontSize: 12 }}>
          {customer_id ?? "—"}
        </Text>
      )}
    </InfoCard>
  );
}

function CustomerCell({ customer_details, customer_id }) {
  const cd = customer_details;
  if (!cd)
    return (
      <Text type="secondary" code style={{ fontSize: 12 }}>
        {customer_id ?? "—"}
      </Text>
    );
  return (
    <Row gutter={[0, 2]}>
      <Col span={24}>
        <Text strong>{cd.full_name || "—"}</Text>
      </Col>
      <Col span={24}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {cd.phone_number || cd.email_id || ""}
        </Text>
      </Col>
    </Row>
  );
}

/** Expandable row content for the Orders table — shows order_items */
export function OrderItemsExpanded({ record }) {
  const items = record.order_items ?? [];
  if (!items.length) {
    return (
      <div style={{ padding: "12px 32px" }}>
        <Text type="secondary">No items</Text>
      </div>
    );
  }

  return (
    <div style={{ background: "var(--muted)", padding: "12px 24px" }}>
      <Text
        type="secondary"
        style={{
          fontSize: 11,
          textTransform: "uppercase",
          letterSpacing: 1,
          display: "block",
          marginBottom: 8,
        }}
      >
        Order Items ({items.length})
      </Text>
      <div className="space-y-2">
        {items.map((item, i) => (
          <div
            key={item.order_item_id ?? i}
            className="rounded-xl bg-card border border-border/50 px-4 py-3"
          >
            <Row gutter={[16, 8]} align="middle">
              <Col xs={24} sm={14}>
                <Text strong>
                  {item.product_details?.name ??
                    item.event_metadata?.title ??
                    "—"}
                </Text>
                {item.event_metadata?.event_name && (
                  <div style={{ marginTop: 2 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {item.event_metadata.event_name}
                      {item.event_metadata.event_type &&
                        ` · ${item.event_metadata.event_type}`}
                    </Text>
                  </div>
                )}
                <div style={{ marginTop: 4 }}>
                  {item.product_details?.product_type && (
                    <Tag style={{ fontSize: 10, margin: 0, marginRight: 4 }}>
                      {item.product_details.product_type}
                    </Tag>
                  )}
                  {item.product_details?.product_domain && (
                    <Tag color="blue" style={{ fontSize: 10, margin: 0 }}>
                      {item.product_details.product_domain}
                    </Tag>
                  )}
                </div>
              </Col>
              <Col xs={6} sm={3} style={{ textAlign: "center" }}>
                <Text
                  type="secondary"
                  style={{ fontSize: 12, display: "block" }}
                >
                  Qty
                </Text>
                <Text strong>{item.quantity}</Text>
              </Col>
              <Col xs={6} sm={3} style={{ textAlign: "center" }}>
                <Text
                  type="secondary"
                  style={{ fontSize: 12, display: "block" }}
                >
                  Unit
                </Text>
                <Text>{item.unit_price}</Text>
              </Col>
              <Col xs={6} sm={2} style={{ textAlign: "center" }}>
                <Text
                  type="secondary"
                  style={{ fontSize: 12, display: "block" }}
                >
                  MRP
                </Text>
                <Text delete type="secondary">
                  {item.mrp}
                </Text>
              </Col>
              <Col xs={6} sm={2} style={{ textAlign: "center" }}>
                <Text
                  type="secondary"
                  style={{ fontSize: 12, display: "block" }}
                >
                  Total
                </Text>
                <Text strong style={{ color: "var(--primary)" }}>
                  {item.line_total}
                </Text>
              </Col>
            </Row>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Expandable row content for the Bookings table — shows product, event & pricing */
export function BookingItemExpanded({ record }) {
  const pd = record.product_details;
  const ed = record.event_details ?? record.event_metadata;
  const pricing = record.pricing;
  const cd = record.customer_details;

  const hasContent = pd || ed || pricing;
  if (!hasContent) {
    return (
      <div style={{ padding: "12px 32px" }}>
        <Text type="secondary">No details available</Text>
      </div>
    );
  }

  return (
    <div style={{ background: "var(--muted)", padding: "12px 24px" }}>
      <Row gutter={[16, 16]}>
        {/* Product details */}
        {pd && (
          <Col xs={24} sm={10}>
            <div className="rounded-xl bg-card border border-border/50 px-4 py-3 h-full">
              <Text
                type="secondary"
                style={{
                  fontSize: 11,
                  textTransform: "uppercase",
                  letterSpacing: 1,
                  display: "block",
                  marginBottom: 6,
                }}
              >
                Product
              </Text>
              <Text strong style={{ display: "block" }}>
                {pd.name || "—"}
              </Text>
              {pd.description && (
                <Text
                  type="secondary"
                  style={{ fontSize: 12, display: "block", marginTop: 2 }}
                >
                  {pd.description}
                </Text>
              )}
              <Row gutter={4} style={{ marginTop: 6 }}>
                {pd.product_domain && (
                  <Col>
                    <Tag
                      color="blue"
                      style={{ fontSize: 10, margin: 0, marginRight: 4 }}
                    >
                      {pd.product_domain}
                    </Tag>
                  </Col>
                )}
              </Row>
            </div>
          </Col>
        )}

        {/* Event details */}
        {ed && (
          <Col xs={24} sm={9}>
            <div className="rounded-xl bg-card border border-border/50 px-4 py-3 h-full">
              <Text
                type="secondary"
                style={{
                  fontSize: 11,
                  textTransform: "uppercase",
                  letterSpacing: 1,
                  display: "block",
                  marginBottom: 6,
                }}
              >
                Event
              </Text>
              <Text strong style={{ display: "block" }}>
                {ed.event_name ?? ed.title ?? "—"}
              </Text>
              {ed.event_type && (
                <Text
                  type="secondary"
                  style={{ fontSize: 12, display: "block", marginTop: 2 }}
                >
                  {ed.event_type}
                </Text>
              )}
              {ed.start_time && (
                <Text
                  type="secondary"
                  style={{ fontSize: 12, display: "block", marginTop: 4 }}
                >
                  {formatDateTime(ed.start_time)}
                  {ed.end_time && ` → ${formatDateTime(ed.end_time)}`}
                </Text>
              )}
            </div>
          </Col>
        )}

        {/* Pricing */}
        {pricing && (
          <Col xs={24} sm={5}>
            <div className="rounded-xl bg-card border border-border/50 px-4 py-3 h-full">
              <Text
                type="secondary"
                style={{
                  fontSize: 11,
                  textTransform: "uppercase",
                  letterSpacing: 1,
                  display: "block",
                  marginBottom: 6,
                }}
              >
                Pricing
              </Text>
              <Row gutter={[0, 4]}>
                {pricing.unit_price != null && (
                  <Col span={24}>
                    <Row justify="space-between">
                      <Col>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Unit
                        </Text>
                      </Col>
                      <Col>
                        <Text style={{ fontSize: 12 }}>
                          {pricing.currency} {pricing.unit_price}
                        </Text>
                      </Col>
                    </Row>
                  </Col>
                )}
                {pricing.discount > 0 && (
                  <Col span={24}>
                    <Row justify="space-between">
                      <Col>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Discount
                        </Text>
                      </Col>
                      <Col>
                        <Text type="success" style={{ fontSize: 12 }}>
                          - {pricing.currency} {pricing.discount}
                        </Text>
                      </Col>
                    </Row>
                  </Col>
                )}
                {getBookingCodeDiscountLabel(pricing) && (
                  <Col span={24}>
                    <Row justify="space-between">
                      <Col>
                        <Text type="secondary" className="text-xs">
                          {getBookingCodeDiscountLabel(pricing)}
                        </Text>
                      </Col>
                      <Col>
                        <Text type="success" className="text-xs">
                          - {pricing.currency} {pricing.code_discount}
                        </Text>
                      </Col>
                    </Row>
                  </Col>
                )}
                {pricing.taxes > 0 && (
                  <Col span={24}>
                    <Row justify="space-between">
                      <Col>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Tax
                        </Text>
                      </Col>
                      <Col>
                        <Text style={{ fontSize: 12 }}>
                          {pricing.currency} {pricing.taxes}
                        </Text>
                      </Col>
                    </Row>
                  </Col>
                )}
                {pricing.total_price != null && (
                  <Col
                    span={24}
                    style={{
                      borderTop: "1px solid var(--border)",
                      paddingTop: 4,
                      marginTop: 2,
                    }}
                  >
                    <Row justify="space-between">
                      <Col>
                        <Text strong style={{ fontSize: 12 }}>
                          Total
                        </Text>
                      </Col>
                      <Col>
                        <Text
                          strong
                          style={{ fontSize: 12, color: "var(--primary)" }}
                        >
                          {pricing.currency} {getBookingTotal(pricing)}
                        </Text>
                      </Col>
                    </Row>
                  </Col>
                )}
              </Row>
            </div>
          </Col>
        )}
      </Row>
    </div>
  );
}

export function getOrderColumns({ onView }) {
  return [
    {
      title: "Order #",
      dataIndex: "order_number",
      key: "order_number",
      render: (v) => (
        <Text code style={{ fontSize: 12 }}>
          {v}
        </Text>
      ),
    },
    {
      title: "Type",
      dataIndex: "order_type",
      key: "order_type",
      render: (v) =>
        v ? <Tag style={{ fontSize: 11 }}>{v.replace(/_/g, " ")}</Tag> : "—",
    },
    {
      title: "Customer",
      key: "customer",
      render: (_, r) => (
        <CustomerCell
          customer_details={r.customer_details}
          customer_id={r.customer_id}
        />
      ),
    },
    {
      title: "Grand Total",
      key: "total",
      render: (_, r) => <Text strong>{getGrandTotal(r.order_totals)}</Text>,
    },
    {
      title: "Status",
      dataIndex: "order_status",
      key: "order_status",
      render: orderStatusTag,
    },
    {
      title: "Ordered At",
      dataIndex: "ordered_at",
      key: "ordered_at",
      render: formatDate,
    },
    {
      title: "Confirmed At",
      dataIndex: "confirmed_at",
      key: "confirmed_at",
      render: formatDate,
    },
    {
      title: "",
      key: "actions",
      width: 60,
      render: (_, r) => (
        <Space size={4}>
          <Tooltip title="View Details">
            <Button
              type="text"
              size="small"
              icon={<Eye className="h-4 w-4" />}
              onClick={() => onView(r)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];
}

export function getBookingColumns({ onView }) {
  return [
    {
      title: "Booking #",
      dataIndex: "booking_number",
      key: "booking_number",
      render: (v) => (
        <Text code style={{ fontSize: 12 }}>
          {v}
        </Text>
      ),
    },
    {
      title: "Customer",
      key: "customer",
      render: (_, r) => (
        <CustomerCell
          customer_details={r.customer_details}
          customer_id={r.customer_id}
        />
      ),
    },
    {
      title: "Event / Product",
      key: "product",
      render: (_, r) =>
        r.product_details?.name ??
        r.event_name ??
        r.event_metadata?.title ??
        "—",
    },
    {
      title: "Type",
      dataIndex: "booking_type",
      key: "booking_type",
      render: (v) => (v ? <Tag style={{ fontSize: 11 }}>{v}</Tag> : "—"),
    },
    {
      title: "Status",
      dataIndex: "booking_status",
      key: "booking_status",
      render: bookingStatusTag,
    },
    {
      title: "Qty",
      dataIndex: "quantity",
      key: "quantity",
    },
    {
      title: "Confirmed At",
      dataIndex: "booking_confirmed_at",
      key: "booking_confirmed_at",
      render: formatDate,
    },
    {
      title: "",
      key: "actions",
      width: 60,
      render: (_, r) => (
        <Space size={4}>
          <Tooltip title="View Details">
            <Button
              type="text"
              size="small"
              icon={<Eye className="h-4 w-4" />}
              onClick={() => onView(r)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];
}
